import type { FC } from 'react'
import { useMemo, useState } from 'react'

import { Input, LoaderCircle, Popup } from '@skytech/ui'
import { Button } from '@skytech/ui/Button'
import { Container } from '@skytech/ui/Container'
import { useQuery } from '@tanstack/react-query'
import PlusIcon from 'mdi-react/PlusIcon'
import { useNavigate, useSearchParams } from 'react-router-dom'

import { Translation } from '@/app/translations/Translation'
import { useTranslation } from '@/app/translations/translations-hooks'
import { useSearch } from '@/hooks/searchParams'
import { APP_ROUTES } from '@/util/app-routes'

import queries from '../queries'
import adminResources from '../resources'

import { TextTemplateForm } from './TextTemplateForm'
import { TextTemplateList } from './TextTemplateList'

export const TextTemplates: FC = () => {
  const navigate = useNavigate()
  const translate = useTranslation()
  const { searchFilter, setSearchFilter } = useSearch()
  const [searchParams, setSearchParams] = useSearchParams()
  const [isOpen, setIsOpen] = useState(false)

  const textTemplateId = Number(searchParams.get('id'))

  const onClickRow = (id: number) => {
    setSearchParams({ id: id.toString() })
    setIsOpen(true)
  }

  const onClose = () => {
    setSearchParams({})
    setIsOpen(false)
  }

  const {
    data: textTemplates = [],
    isSuccess,
    isLoading,
  } = useQuery(queries.adminTextTemplates(), adminResources.getTextTemplateList)

  // select distinct value code from textTemplates;
  const codeOptions = useMemo(() => {
    const codeOptions: string[] = []
    textTemplates.forEach(textTemplate => {
      if (!codeOptions.includes(textTemplate.code)) {
        codeOptions.push(textTemplate.code)
      }
    })
    return codeOptions.map(code => ({ label: code, value: code }))
  }, [textTemplates])

  if (isLoading) {
    return (
      <Container className="text-templates">
        <LoaderCircle />
      </Container>
    )
  }

  return (
    <Container onSwipedLeft={() => navigate(APP_ROUTES.HOME)} className="text-templates">
      <Container.Header>
        <Translation id="Text Templates" />
      </Container.Header>
      <div className="table-actions">
        <Button
          Icon={<PlusIcon />}
          disabled={!isSuccess}
          className="mb-4 ml-0"
          onClick={() => setIsOpen(true)}
        >
          <Translation id="New template" />
        </Button>

        <Input
          id="searchFilter"
          type="text"
          value={searchFilter}
          placeholder={translate('Search')}
          marginBottom={16}
          onChange={e => setSearchFilter(e.target.value)}
        />
      </div>
      <TextTemplateList
        searchFilter={searchFilter}
        onClickTextTemplateRow={onClickRow}
        textTemplateList={textTemplates}
      />
      <Popup
        variant="narrow"
        ariaLabel={translate('Add or edit text template')}
        isOpen={isOpen || !!textTemplateId}
        onClose={onClose}
      >
        <TextTemplateForm
          codeOptions={codeOptions}
          onClose={onClose}
          textTemplateId={textTemplateId}
        />
      </Popup>
    </Container>
  )
}
